import { llmRequest, LlmError } from "./client";
import type { LlmContentBlock } from "./client";
import { VACANCY_EXTRACTION_SYSTEM_PROMPT } from "./prompts";

export interface ExtractedVacancy {
  title: string | null;
  client: string | null;
  portal: string | null;
  rate: number | null;
  hours_per_week: number | null;
  location: string | null;
  remote_policy: "remote" | "hybrid" | "onsite" | null;
  start_date: string | null;
  duration_months: number | null;
  zzp_allowed: boolean | null;
  dutch_language_requirement: string | null;
  deadline: string | null;
  requirements: {
    hard: string[];
    preferred: string[];
    competencies: string[];
  };
  full_text: string | null;
}

const schema = {
  type: "object",
  properties: {
    title: { type: "string" },
    client: { type: "string" },
    portal: { type: "string" },
    rate: { type: ["number", "null"] },
    hours_per_week: { type: ["number", "null"] },
    location: { type: "string" },
    remote_policy: { type: "string", enum: ["remote", "hybrid", "onsite"] },
    start_date: { type: "string" },
    duration_months: { type: ["number", "null"] },
    zzp_allowed: { type: "boolean" },
    dutch_language_requirement: { type: "string" },
    deadline: { type: "string", description: "ISO 8601 UTC date string" },
    requirements: {
      type: "object",
      properties: {
        hard: { type: "array", items: { type: "string" } },
        preferred: { type: "array", items: { type: "string" } },
        competencies: { type: "array", items: { type: "string" } },
      },
      required: ["hard", "preferred", "competencies"],
    },
    full_text: { type: "string" },
  },
  required: [
    "title", "client", "portal", "rate", "hours_per_week", "location",
    "remote_policy", "start_date", "duration_months", "zzp_allowed",
    "dutch_language_requirement", "deadline", "requirements", "full_text",
  ],
};

export async function extractVacancyFromScreenshot(
  _apiKey: string,     // kept for call-site compat during transition; ignored — client reads from store
  _model: string,
  base64PngData: string,
  url?: string,
  systemPrompt?: string | null,
): Promise<ExtractedVacancy> {
  const userContent: LlmContentBlock[] = [
    {
      type: "image",
      base64: base64PngData,
      mimeType: "image/png",
    },
    {
      type: "text",
      text: `Please extract the vacancy details from this screenshot. Optional vacancy page URL: ${url || "not provided"}`,
    },
  ];

  return llmRequest<ExtractedVacancy>({
    systemPrompt: systemPrompt || VACANCY_EXTRACTION_SYSTEM_PROMPT,
    userContent,
    schema,
    temperature: 0.2,
  });
}

export async function extractVacancyFromPdf(
  pdfText: string,
  url?: string,
  systemPrompt?: string | null,
): Promise<ExtractedVacancy> {
  const userContent: LlmContentBlock[] = [
    {
      type: "document",
      text: pdfText,
    },
    {
      type: "text",
      text: `Please extract the vacancy details from the above text. Optional vacancy page URL: ${url || "not provided"}`,
    },
  ];

  return llmRequest<ExtractedVacancy>({
    systemPrompt: systemPrompt || VACANCY_EXTRACTION_SYSTEM_PROMPT,
    userContent,
    schema,
    temperature: 0.2,
  });
}

export { LlmError };
